import { apiSlice } from '../../../store/api/apiSlice';

// Types
export type FlagType = 'rollout' | 'experiment' | 'ops';
export type TargetRole = 'coach' | 'member' | 'all';

export interface FeatureFlag {
  key: string;
  description?: string;
  flag_type: FlagType;
  target_role: TargetRole;
  enabled_for_all: boolean;
  allow_related_member_reads: boolean;
  allow_related_coach_reads: boolean;
  created_by?: string;
  created_at: string;
  enabled_person_count: number;
}

export interface PersonAssignment {
  person_id: string;
  email: string;
  name: string;
  created_at: string;
}

export interface FlagPeopleResponse {
  flag_key: string;
  target_role: TargetRole;
  enabled_for_all: boolean;
  allow_related_member_reads: boolean;
  allow_related_coach_reads: boolean;
  people: PersonAssignment[];
}

export interface ListFlagsResponse {
  flags: FeatureFlag[];
}

export interface CreateFlagRequest {
  flag_key: string;
  description?: string;
  flag_type: FlagType;
  target_role: TargetRole;
  allow_related_member_reads?: boolean;
  allow_related_coach_reads?: boolean;
}

export interface UpdateFlagRequest {
  description?: string;
  flag_type?: FlagType;
  enabled_for_all?: boolean;
  allow_related_member_reads?: boolean;
  allow_related_coach_reads?: boolean;
}

export type BulkCoachAssignmentStatus =
  | 'ready'
  | 'already_assigned'
  | 'duplicate'
  | 'invalid_email'
  | 'unknown_person'
  | 'ineligible_coach';

export interface BulkCoachAssignmentEntry {
  line: number;
  input: string;
  email?: string;
  status: BulkCoachAssignmentStatus;
  reason?: 'disabled' | 'not_operational_coach';
  duplicate_of_line?: number;
  person_id?: string;
  name?: string;
}

export interface BulkCoachAssignmentReview {
  flag_key: string;
  target_role: TargetRole;
  review_fingerprint: string;
  can_apply: boolean;
  entries: BulkCoachAssignmentEntry[];
  summary: {
    ready: number;
    already_assigned: number;
    blocking: number;
  };
}

export interface BulkCoachAssignmentApplyResult {
  review: BulkCoachAssignmentReview;
  created_count: number;
  created_person_ids: string[];
}

export interface BulkCoachAssignmentErrorResponse {
  error: string;
  message: string;
  review?: BulkCoachAssignmentReview;
}

// API endpoints
export const featureFlagsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // List flags with optional target_role filter
    listFeatureFlags: builder.query<ListFlagsResponse, { targetRole?: TargetRole } | undefined>({
      query: (args) => {
        const targetRole = args && 'targetRole' in args ? args.targetRole : undefined;
        const params = targetRole ? `?target_role=${targetRole}` : '';
        return `/admin/feature-flags${params}`;
      },
      providesTags: (result) => [
        'FeatureFlags',
        ...(result?.flags.map((f) => ({ type: 'FeatureFlag' as const, id: f.key })) ?? []),
      ],
    }),

    // Get single flag details
    getFlag: builder.query<{ flag: FeatureFlag }, { flagKey: string }>({
      query: ({ flagKey }) => `/admin/feature-flags/${encodeURIComponent(flagKey)}`,
      providesTags: (_result, _error, { flagKey }) => [{ type: 'FeatureFlag', id: flagKey }],
    }),

    // Create a new flag
    createFlag: builder.mutation<{ flag: FeatureFlag }, CreateFlagRequest>({
      query: (body) => ({
        url: '/admin/feature-flags',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FeatureFlags'],
    }),

    // Update a flag
    updateFeatureFlag: builder.mutation<
      { flag: FeatureFlag },
      { flagKey: string } & UpdateFlagRequest
    >({
      query: ({ flagKey, ...body }) => ({
        url: `/admin/feature-flags/${encodeURIComponent(flagKey)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { flagKey }) => [
        'FeatureFlags',
        { type: 'FeatureFlag', id: flagKey },
        { type: 'FeatureFlagPeople', id: flagKey },
      ],
    }),

    // Delete a flag
    deleteFlag: builder.mutation<{ ok: boolean }, { flagKey: string }>({
      query: ({ flagKey }) => ({
        url: `/admin/feature-flags/${encodeURIComponent(flagKey)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FeatureFlags'],
    }),

    // Get people assigned to a flag
    getFlagPeople: builder.query<FlagPeopleResponse, { flagKey: string }>({
      query: ({ flagKey }) => `/admin/feature-flags/${encodeURIComponent(flagKey)}/people`,
      providesTags: (_result, _error, { flagKey }) => [{ type: 'FeatureFlagPeople', id: flagKey }],
    }),

    // Add a person to a flag
    addPersonToFlag: builder.mutation<{ ok: boolean }, { flagKey: string; personId: string }>({
      query: ({ flagKey, personId }) => ({
        url: `/admin/feature-flags/${encodeURIComponent(flagKey)}/people`,
        method: 'POST',
        body: { person_id: personId },
      }),
      invalidatesTags: (_result, _error, { flagKey }) => [
        'FeatureFlags',
        { type: 'FeatureFlag', id: flagKey },
        { type: 'FeatureFlagPeople', id: flagKey },
      ],
    }),

    reviewBulkCoachAssignments: builder.mutation<
      BulkCoachAssignmentReview,
      { flagKey: string; emailsText: string }
    >({
      query: ({ flagKey, emailsText }) => ({
        url: `/admin/feature-flags/${encodeURIComponent(flagKey)}/people/bulk/review`,
        method: 'POST',
        body: { emails_text: emailsText },
      }),
    }),

    applyBulkCoachAssignments: builder.mutation<
      BulkCoachAssignmentApplyResult,
      { flagKey: string; emailsText: string; reviewFingerprint: string }
    >({
      query: ({ flagKey, emailsText, reviewFingerprint }) => ({
        url: `/admin/feature-flags/${encodeURIComponent(flagKey)}/people/bulk/apply`,
        method: 'POST',
        body: {
          emails_text: emailsText,
          review_fingerprint: reviewFingerprint,
        },
      }),
      invalidatesTags: (_result, _error, { flagKey }) => [
        'FeatureFlags',
        { type: 'FeatureFlag', id: flagKey },
        { type: 'FeatureFlagPeople', id: flagKey },
      ],
    }),

    // Remove a person from a flag
    removePersonFromFlag: builder.mutation<{ ok: boolean }, { flagKey: string; personId: string }>({
      query: ({ flagKey, personId }) => ({
        url: `/admin/feature-flags/${encodeURIComponent(flagKey)}/people/${personId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { flagKey }) => [
        'FeatureFlags',
        { type: 'FeatureFlag', id: flagKey },
        { type: 'FeatureFlagPeople', id: flagKey },
      ],
    }),
  }),
});

export const {
  useListFeatureFlagsQuery,
  useGetFlagQuery,
  useCreateFlagMutation,
  useUpdateFeatureFlagMutation,
  useDeleteFlagMutation,
  useGetFlagPeopleQuery,
  useAddPersonToFlagMutation,
  useReviewBulkCoachAssignmentsMutation,
  useApplyBulkCoachAssignmentsMutation,
  useRemovePersonFromFlagMutation,
} = featureFlagsApi;

// Legacy aliases for backward compatibility
export const useGetFlagCoachesQuery = useGetFlagPeopleQuery;
export const useAddCoachToFlagMutation = useAddPersonToFlagMutation;
export const useRemoveCoachFromFlagMutation = useRemovePersonFromFlagMutation;
